import { Component } from "react";
import { Loading as LoadingComponent } from "views/components";
import InfoAlertComponent from "views/components/InfoAlert/InfoAlert";
import PlusIconComponent from "views/components/icons/PlusIcon";
import DesignSizeIconComponent from "views/components/icons/DesignSizeIcon";
import BrandKitEmpty from "../BrandKitEmpty/BrandKitEmpty";
import BrandKitSizesRow from "./BrandKitSizesRow";
import AddTeamTemplateSizeModalContainer from "./modals/AddTeamTemplateSizeModalContainer";
import UpdateTeamTemplateSizeModalContainer from "./modals/UpdateTeamTemplateSizeModalContainer";
import confirmDeleteTeamTemplateSize from "./modals/confirmDeleteTeamTemplateSize";
import { TeamTemplateSize } from "state/entities/teamTemplateSizes/teamTemplateSizesActions";
import style from "./style.module.css";

const Loading = LoadingComponent as any;
const InfoAlert = InfoAlertComponent as any;
const PlusIcon = PlusIconComponent as any;
const DesignSizeIcon = DesignSizeIconComponent as any;

interface BrandKitSizesProps {
  teamId: string;
  sizes: TeamTemplateSize[];
  isFetching: boolean;
  isBrandManager: boolean;
  fetchTeamTemplateSizesIfNeeded: (args: { teamId: string }) => void;
  deleteTeamTemplateSize: (args: {
    teamId: string;
    sizeId: string;
    onSuccess?: () => void;
  }) => void;
}

interface BrandKitSizesState {
  isAddModalOpen: boolean;
  isUpdateModalOpen: boolean;
  selectedSize: TeamTemplateSize | null;
}

class BrandKitSizes extends Component<BrandKitSizesProps, BrandKitSizesState> {
  state: BrandKitSizesState = {
    isAddModalOpen: false,
    isUpdateModalOpen: false,
    selectedSize: null
  };

  componentDidMount() {
    const { teamId, fetchTeamTemplateSizesIfNeeded } = this.props;
    if (teamId) {
      fetchTeamTemplateSizesIfNeeded({ teamId });
    }
  }

  openAddModal = () => this.setState({ isAddModalOpen: true });

  closeAddModal = () => this.setState({ isAddModalOpen: false });

  openUpdateModal = (size: TeamTemplateSize) =>
    this.setState({ isUpdateModalOpen: true, selectedSize: size });

  closeUpdateModal = () =>
    this.setState({ isUpdateModalOpen: false, selectedSize: null });

  handleDelete = (size: TeamTemplateSize) => {
    const { deleteTeamTemplateSize, teamId } = this.props;
    confirmDeleteTeamTemplateSize({
      size,
      onConfirm: () => deleteTeamTemplateSize({ teamId, sizeId: size.id })
    });
  };

  renderList() {
    const { sizes, isBrandManager } = this.props;
    return sizes.map(size => (
      <BrandKitSizesRow
        key={size.id}
        size={size}
        isBrandManager={isBrandManager}
        onEdit={this.openUpdateModal}
        onDelete={this.handleDelete}
      />
    ));
  }

  render() {
    const { sizes, isFetching, isBrandManager } = this.props;
    const { isAddModalOpen, isUpdateModalOpen, selectedSize } = this.state;
    const hasSizes = sizes.length > 0;

    return (
      <>
        <div
          className={style.brandKitSizesWrapper}
          data-testid="brandKitSizesWrapper"
        >
          <div className={style.headerRow}>
            <div className={style.contentTitle}>Brand Sizes</div>
            {isBrandManager && (
              <button
                className={style.addButton}
                onClick={this.openAddModal}
                data-testid="addSizeButton"
                type="button"
              >
                <PlusIcon color="#ffffff" size="16px" />
                <span>Add size</span>
              </button>
            )}
          </div>

          <InfoAlert
            className={style.info}
            theme={"blue"}
            hasIcon={true}
            spanClass={style.infoText}
          >
            Save your team&apos;s custom canvas dimensions so every member can
            create designs at the right size.
          </InfoAlert>

          {hasSizes && this.renderList()}

          {!hasSizes && !isFetching && (
            <BrandKitEmpty
              icon={<DesignSizeIcon height="48px" width="48px" />}
              title="No custom sizes yet"
              subtitle={
                isBrandManager
                  ? "Add a size so your team can reuse it when creating new designs."
                  : "Your brand manager can add custom sizes for your team to reuse."
              }
              button={
                isBrandManager
                  ? {
                      onClick: this.openAddModal,
                      children: "Add a size"
                    }
                  : undefined
              }
            />
          )}
        </div>

        {isFetching && (
          <div className={style.loader}>
            <Loading size="24px" />
          </div>
        )}

        {isAddModalOpen && (
          <AddTeamTemplateSizeModalContainer
            isOpen={isAddModalOpen}
            onCloseModal={this.closeAddModal}
          />
        )}

        {isUpdateModalOpen && selectedSize && (
          <UpdateTeamTemplateSizeModalContainer
            isOpen={isUpdateModalOpen}
            selectedSize={selectedSize}
            onCloseModal={this.closeUpdateModal}
          />
        )}
      </>
    );
  }
}

export default BrandKitSizes;
